// Seed location data used only for examples and explicit text matching.
// Real GPS coordinates must be resolved through reverse geocoding and must
// never be silently mapped to one of these sample cities.
export type Locale = {
  /** Identidade/pesquisa: nome canónico usado em POIs, rotas e Maps. */
  city: string;
  /** Apresentação ao utilizador (exónimo escrito no pedido). Opcional. */
  displayCity?: string;
  /** Fornecedor que resolveu o destino (identidade estável). */
  provider?: "osm" | "google";
  /** ID do fornecedor; coordenadas são apenas fallback de identidade. */
  providerPlaceId?: string;
  country: string;
  countryCode: string;

  lat: number;
  lng: number;
  currency: string;
  currencySymbol: string;
  language: string; // BCP-47
  stops: Stop[];
};

export type Stop = {
  name: string;
  category: string;
  walkMin: number;
  scooterMin: number;
  bikeMin: number;
  priceLocal: number; // local currency
  rating: number;
  blurb: Record<string, string>; // language -> text
};

export const LOCATIONS: Record<string, Locale> = {
  paris: {
    city: "Paris", country: "France", countryCode: "FR",
    lat: 48.8566, lng: 2.3522, currency: "EUR", currencySymbol: "€", language: "fr",
    stops: [
      { name: "Musée du Louvre", category: "Musée", walkMin: 14, scooterMin: 5, bikeMin: 7, priceLocal: 22, rating: 4.8,
        blurb: { fr: "Le plus grand musée d'art au monde.", en: "The world's largest art museum.", es: "El museo de arte más grande del mundo.", ja: "世界最大の美術館。", de: "Das größte Kunstmuseum der Welt." } },
      { name: "Café de Flore", category: "Café", walkMin: 9, scooterMin: 3, bikeMin: 4, priceLocal: 8, rating: 4.5,
        blurb: { fr: "Café littéraire emblématique.", en: "Iconic literary café.", es: "Café literario icónico.", ja: "象徴的な文学カフェ。", de: "Legendäres Literaturcafé." } },
      { name: "Tour Eiffel", category: "Monument", walkMin: 28, scooterMin: 11, bikeMin: 15, priceLocal: 29, rating: 4.7,
        blurb: { fr: "L'icône intemporelle de Paris.", en: "Paris's timeless icon.", es: "El ícono eterno de París.", ja: "パリの永遠の象徴。", de: "Das zeitlose Wahrzeichen von Paris." } },
    ],
  },
  tokyo: {
    city: "Tokyo", country: "Japan", countryCode: "JP",
    lat: 35.6762, lng: 139.6503, currency: "JPY", currencySymbol: "¥", language: "ja",
    stops: [
      { name: "浅草寺 (Sensō-ji)", category: "寺院", walkMin: 12, scooterMin: 4, bikeMin: 6, priceLocal: 0, rating: 4.7,
        blurb: { fr: "Le plus ancien temple de Tokyo.", en: "Tokyo's oldest temple.", es: "El templo más antiguo de Tokio.", ja: "東京最古の寺院。", de: "Tokios ältester Tempel." } },
      { name: "Shibuya Crossing", category: "Landmark", walkMin: 22, scooterMin: 9, bikeMin: 12, priceLocal: 0, rating: 4.6,
        blurb: { fr: "Le carrefour le plus animé du monde.", en: "The world's busiest intersection.", es: "El cruce más concurrido del mundo.", ja: "世界一忙しい交差点。", de: "Die belebteste Kreuzung der Welt." } },
      { name: "Tsukiji Outer Market", category: "Food", walkMin: 18, scooterMin: 7, bikeMin: 10, priceLocal: 1500, rating: 4.8,
        blurb: { fr: "Paradis du sushi de rue.", en: "Street sushi paradise.", es: "Paraíso del sushi callejero.", ja: "屋台寿司の楽園。", de: "Paradies für Straßen-Sushi." } },
    ],
  },
  "new york": {
    city: "New York", country: "United States", countryCode: "US",
    lat: 40.7128, lng: -74.006, currency: "USD", currencySymbol: "$", language: "en",
    stops: [
      { name: "Central Park", category: "Park", walkMin: 16, scooterMin: 6, bikeMin: 8, priceLocal: 0, rating: 4.8,
        blurb: { fr: "Le poumon vert de Manhattan.", en: "Manhattan's green lung.", es: "El pulmón verde de Manhattan.", ja: "マンハッタンの緑の肺。", de: "Die grüne Lunge Manhattans." } },
      { name: "MoMA", category: "Museum", walkMin: 11, scooterMin: 4, bikeMin: 6, priceLocal: 30, rating: 4.6,
        blurb: { fr: "Musée d'art moderne de référence.", en: "World-class modern art museum.", es: "Museo de arte moderno de referencia.", ja: "世界的な近代美術館。", de: "Weltklasse-Museum für moderne Kunst." } },
      { name: "Brooklyn Bridge", category: "Landmark", walkMin: 24, scooterMin: 9, bikeMin: 13, priceLocal: 0, rating: 4.9,
        blurb: { fr: "Traversée légendaire au-dessus de l'East River.", en: "Legendary walk over the East River.", es: "Caminata legendaria sobre el East River.", ja: "イーストリバーを渡る伝説の橋。", de: "Legendärer Spaziergang über den East River." } },
    ],
  },
  "mexico city": {
    city: "Mexico City", country: "Mexico", countryCode: "MX",
    lat: 19.4326, lng: -99.1332, currency: "MXN", currencySymbol: "$", language: "es",
    stops: [
      { name: "Zócalo", category: "Plaza", walkMin: 10, scooterMin: 4, bikeMin: 5, priceLocal: 0, rating: 4.7,
        blurb: { fr: "Le cœur historique de la ville.", en: "The historic heart of the city.", es: "El corazón histórico de la ciudad.", ja: "市の歴史的中心。", de: "Das historische Herz der Stadt." } },
      { name: "Mercado de Coyoacán", category: "Mercado", walkMin: 19, scooterMin: 7, bikeMin: 10, priceLocal: 120, rating: 4.6,
        blurb: { fr: "Saveurs et artisanat locaux.", en: "Local flavors and crafts.", es: "Sabores y artesanías locales.", ja: "地元の味と工芸品。", de: "Lokale Aromen und Handwerk." } },
      { name: "Museo Frida Kahlo", category: "Museo", walkMin: 22, scooterMin: 9, bikeMin: 12, priceLocal: 270, rating: 4.8,
        blurb: { fr: "La Casa Azul de Frida.", en: "Frida's Blue House.", es: "La Casa Azul de Frida.", ja: "フリーダの青い家。", de: "Fridas Blaues Haus." } },
    ],
  },
  lisbon: {
    city: "Lisboa", country: "Portugal", countryCode: "PT",
    lat: 38.7223, lng: -9.1393, currency: "EUR", currencySymbol: "€", language: "pt",
    stops: [
      { name: "Torre de Belém", category: "Monumento", walkMin: 18, scooterMin: 7, bikeMin: 10, priceLocal: 8, rating: 4.7,
        blurb: { pt: "Símbolo dos Descobrimentos à beira-Tejo.", en: "Riverside symbol of the Age of Discovery.", fr: "Symbole des grandes découvertes au bord du Tage.", es: "Símbolo de los Descubrimientos junto al Tajo.", ja: "テージョ川沿いの大航海時代の象徴。", de: "Wahrzeichen des Zeitalters der Entdeckungen am Tejo." } },
      { name: "Time Out Market", category: "Mercado", walkMin: 12, scooterMin: 4, bikeMin: 6, priceLocal: 15, rating: 4.6,
        blurb: { pt: "O melhor da gastronomia lisboeta num só sítio.", en: "The best of Lisbon's food scene under one roof.", fr: "Le meilleur de la gastronomie lisboète réuni.", es: "Lo mejor de la gastronomía lisboeta bajo un techo.", ja: "リスボン美食の決定版。", de: "Lissabons beste Küche unter einem Dach." } },
      { name: "Miradouro da Senhora do Monte", category: "Miradouro", walkMin: 22, scooterMin: 8, bikeMin: 12, priceLocal: 0, rating: 4.8,
        blurb: { pt: "A vista mais bonita sobre os telhados de Alfama.", en: "The finest view over Alfama's rooftops.", fr: "La plus belle vue sur les toits d'Alfama.", es: "La mejor vista sobre los tejados de Alfama.", ja: "アルファマの屋根を望む絶景。", de: "Der schönste Blick über die Dächer Alfamas." } },
    ],
  },
};

export const DEFAULT_KEY = "paris";

/**
 * Destino vazio: nenhuma cidade sugerida enquanto a resolução está pendente ou
 * falha. Nunca usar `LOCATIONS["paris"]` como fallback visual ou funcional —
 * um chip "Roteiro em Paris" contamina o pedido seguinte.
 */
export const EMPTY_LOCALE: Locale = {
  city: "",
  country: "",
  countryCode: "",
  lat: Number.NaN,
  lng: Number.NaN,
  currency: "EUR",
  currencySymbol: "€",
  language: "pt",
  stops: [],
};


export function findLocation(query: string): Locale | null {
  const q = query.trim().toLowerCase();
  if (!q) return null;
  if (LOCATIONS[q]) return LOCATIONS[q];
  for (const k of Object.keys(LOCATIONS)) {
    if (k.includes(q) || LOCATIONS[k].city.toLowerCase().includes(q) || LOCATIONS[k].country.toLowerCase().includes(q))
      return LOCATIONS[k];
  }
  return null;
}

export function locationFromCoords(lat: number, lng: number): Locale {
  // Safety fallback used only when reverse geocoding is unavailable.
  // Do not guess a city from the small seed list: that previously caused GPS
  // positions in northern Portugal to be labelled as Lisboa.
  return {
    city: "Localização atual",
    country: "",
    countryCode: "",
    lat,
    lng,
    currency: "EUR",
    currencySymbol: "€",
    language: "pt",
    stops: [],
  };
}
